import type { ThemeRegistryEntry } from "../contracts";

export const THEME_COLOR_TOKEN_TO_CSS_VARIABLE = {
    "color.background": "--color-background",
    "color.foreground": "--color-foreground",
    "color.card": "--color-card",
    "color.card.foreground": "--color-card-foreground",
    "color.popover": "--color-popover",
    "color.popover.foreground": "--color-popover-foreground",
    "color.primary": "--color-primary",
    "color.primary.foreground": "--color-primary-foreground",
    "color.secondary": "--color-secondary",
    "color.secondary.foreground": "--color-secondary-foreground",
    "color.muted": "--color-muted",
    "color.muted.foreground": "--color-muted-foreground",
    "color.accent": "--color-accent",
    "color.accent.foreground": "--color-accent-foreground",
    "color.destructive": "--color-destructive",
    "color.destructive.foreground": "--color-destructive-foreground",
    "color.border": "--color-border",
    "color.input": "--color-input",
    "color.ring": "--color-ring",
    "color.info": "--color-info",
    "color.success": "--color-success",
    "color.warning": "--color-warning",
    "color.preview.surface": "--color-preview-surface",
    "editor.selection.background": "--editor-selection-background",
    "editor.fold.foreground": "--editor-fold-foreground",
    "editor.fold.hoverForeground": "--editor-fold-hover-foreground",
    "editor.cursor": "--editor-cursor",
    "editor.autocomplete.shadow": "--editor-autocomplete-shadow",
    "editor.hover.shadow": "--editor-hover-shadow",
    "editor.completion.match": "--editor-completion-match",
    "content.link": "--content-link",
    "content.blockquote.border": "--content-blockquote-border",
    "content.inlineRef.background": "--content-inline-ref-background",
    "content.inlineRef.foreground": "--content-inline-ref-foreground",
    "content.inlineRef.border": "--content-inline-ref-border",
    "content.inlineRef.warningBackground": "--content-inline-ref-warning-background",
    "content.inlineRef.warningForeground": "--content-inline-ref-warning-foreground",
    "status.running": "--status-running",
    "status.success": "--status-success",
    "status.warning": "--status-warning",
    "status.error": "--status-error",
    "cm.define.accent": "--cm-define-accent",
    "cm.import.accent": "--cm-import-accent",
    "cm.use.accent": "--cm-use-accent",
    "cm.meta.accent": "--cm-meta-accent",
    "cm.content.accent": "--cm-content-accent",
    "cm.render.accent": "--cm-render-accent",
    "cm.for.accent": "--cm-for-accent",
    "cm.explain.accent": "--cm-explain-accent",
    "cm.ref.resolved": "--cm-ref-resolved",
    "cm.ref.definition": "--cm-ref-definition",
    "cm.ref.scope": "--cm-ref-scope",
    "cm.ref.loop": "--cm-ref-loop",
    "cm.ref.content": "--cm-ref-content",
    "cm.ref.pending": "--cm-ref-pending",
    "cm.ref.running": "--cm-ref-running",
    "cm.ref.error": "--cm-ref-error",
} as const;

export type ThemeColorTokenId = keyof typeof THEME_COLOR_TOKEN_TO_CSS_VARIABLE;

const THEME_COLOR_TOKEN_METADATA = {
    "color.background": {
        description: "App background surface.",
        domain: "foundation",
        public: true,
    },
    "color.foreground": {
        description: "App foreground text.",
        domain: "foundation",
        public: true,
    },
    "color.card": {
        description: "Card surface.",
        domain: "foundation",
        public: true,
    },
    "color.card.foreground": {
        description: "Card foreground.",
        domain: "foundation",
        public: true,
    },
    "color.popover": {
        description: "Popover surface.",
        domain: "foundation",
        public: true,
    },
    "color.popover.foreground": {
        description: "Popover foreground.",
        domain: "foundation",
        public: true,
    },
    "color.primary": {
        description: "Primary accent color.",
        domain: "foundation",
        public: true,
    },
    "color.primary.foreground": {
        description: "Foreground on primary accent.",
        domain: "foundation",
        public: true,
    },
    "color.secondary": {
        description: "Secondary surface.",
        domain: "foundation",
        public: true,
    },
    "color.secondary.foreground": {
        description: "Foreground on secondary surface.",
        domain: "foundation",
        public: true,
    },
    "color.muted": {
        description: "Muted surface.",
        domain: "foundation",
        public: true,
    },
    "color.muted.foreground": {
        description: "Muted foreground.",
        domain: "foundation",
        public: true,
    },
    "color.accent": {
        description: "Accent surface.",
        domain: "foundation",
        public: true,
    },
    "color.accent.foreground": {
        description: "Accent foreground.",
        domain: "foundation",
        public: true,
    },
    "color.destructive": {
        description: "Destructive accent.",
        domain: "foundation",
        public: true,
    },
    "color.destructive.foreground": {
        description: "Foreground on destructive accent.",
        domain: "foundation",
        public: true,
    },
    "color.border": {
        description: "Default border color.",
        domain: "foundation",
        public: true,
    },
    "color.input": {
        description: "Input outline color.",
        domain: "foundation",
        public: true,
    },
    "color.ring": {
        description: "Focus ring color.",
        domain: "foundation",
        public: true,
    },
    "color.info": {
        description: "Info state accent.",
        domain: "state",
        public: true,
    },
    "color.success": {
        description: "Success state accent.",
        domain: "state",
        public: true,
    },
    "color.warning": {
        description: "Warning state accent.",
        domain: "state",
        public: true,
    },
    "color.preview.surface": {
        description: "Preview surface background.",
        domain: "content",
        public: true,
    },
    "editor.selection.background": {
        description: "Editor selection background.",
        domain: "editor",
        public: true,
    },
    "editor.fold.foreground": {
        description: "Fold placeholder foreground.",
        domain: "editor",
        public: true,
    },
    "editor.fold.hoverForeground": {
        description: "Fold placeholder hover foreground.",
        domain: "editor",
        public: true,
    },
    "editor.cursor": {
        description: "Editor cursor color.",
        domain: "editor",
        public: true,
    },
    "editor.autocomplete.shadow": {
        description: "Autocomplete popup shadow.",
        domain: "editor",
        public: true,
    },
    "editor.hover.shadow": {
        description: "Hover popup shadow.",
        domain: "editor",
        public: true,
    },
    "editor.completion.match": {
        description: "Autocomplete matched text color.",
        domain: "editor",
        public: true,
    },
    "content.link": {
        description: "Rendered content link color.",
        domain: "content",
        public: true,
    },
    "content.blockquote.border": {
        description: "Rendered content blockquote border color.",
        domain: "content",
        public: true,
    },
    "content.inlineRef.background": {
        description: "Rendered inline ref background.",
        domain: "content",
        public: true,
    },
    "content.inlineRef.foreground": {
        description: "Rendered inline ref foreground.",
        domain: "content",
        public: true,
    },
    "content.inlineRef.border": {
        description: "Rendered inline ref border.",
        domain: "content",
        public: true,
    },
    "content.inlineRef.warningBackground": {
        description: "Rendered unresolved inline ref background.",
        domain: "content",
        public: true,
    },
    "content.inlineRef.warningForeground": {
        description: "Rendered unresolved inline ref foreground.",
        domain: "content",
        public: true,
    },
    "status.running": {
        description: "Running state accent.",
        domain: "state",
        public: true,
    },
    "status.success": {
        description: "Success state accent.",
        domain: "state",
        public: true,
    },
    "status.warning": {
        description: "Warning state accent.",
        domain: "state",
        public: true,
    },
    "status.error": {
        description: "Error state accent.",
        domain: "state",
        public: true,
    },
    "cm.define.accent": {
        description: "CodeMirror ::define accent.",
        domain: "editor",
        public: true,
    },
    "cm.import.accent": {
        description: "CodeMirror ::import accent.",
        domain: "editor",
        public: true,
    },
    "cm.use.accent": {
        description: "CodeMirror ::use accent.",
        domain: "editor",
        public: true,
    },
    "cm.meta.accent": {
        description: "CodeMirror ::meta accent.",
        domain: "editor",
        public: true,
    },
    "cm.content.accent": {
        description: "CodeMirror ::content accent.",
        domain: "editor",
        public: true,
    },
    "cm.render.accent": {
        description: "CodeMirror ::render accent.",
        domain: "editor",
        public: true,
    },
    "cm.for.accent": {
        description: "CodeMirror ::for accent.",
        domain: "editor",
        public: true,
    },
    "cm.explain.accent": {
        description: "CodeMirror ::explain accent.",
        domain: "editor",
        public: true,
    },
    "cm.ref.resolved": {
        description: "CodeMirror resolved ref accent.",
        domain: "editor",
        public: true,
    },
    "cm.ref.definition": {
        description: "CodeMirror definition ref accent.",
        domain: "editor",
        public: true,
    },
    "cm.ref.scope": {
        description: "CodeMirror scope ref accent.",
        domain: "editor",
        public: true,
    },
    "cm.ref.loop": {
        description: "CodeMirror loop variable ref accent.",
        domain: "editor",
        public: true,
    },
    "cm.ref.content": {
        description: "CodeMirror content ref accent.",
        domain: "editor",
        public: true,
    },
    "cm.ref.pending": {
        description: "CodeMirror pending ref accent.",
        domain: "editor",
        public: true,
    },
    "cm.ref.running": {
        description: "CodeMirror running ref accent.",
        domain: "editor",
        public: true,
    },
    "cm.ref.error": {
        description: "CodeMirror error ref accent.",
        domain: "editor",
        public: true,
    },
} as const satisfies Record<
    ThemeColorTokenId,
    Pick<ThemeRegistryEntry<ThemeColorTokenId>, "description" | "domain" | "public">
>;

export const THEME_COLOR_REGISTRY: ThemeRegistryEntry<ThemeColorTokenId>[] = (
    Object.keys(THEME_COLOR_TOKEN_TO_CSS_VARIABLE) as ThemeColorTokenId[]
).map((id) => ({
    id,
    cssVariable: THEME_COLOR_TOKEN_TO_CSS_VARIABLE[id],
    ...THEME_COLOR_TOKEN_METADATA[id],
}));

export const KNOWN_THEME_COLOR_VARIABLES = Object.values(
    THEME_COLOR_TOKEN_TO_CSS_VARIABLE,
);

export const THEME_COLOR_CSS_VARIABLE_TO_TOKEN = Object.fromEntries(
    Object.entries(THEME_COLOR_TOKEN_TO_CSS_VARIABLE).map(([id, cssVariable]) => [
        cssVariable,
        id,
    ]),
) as Record<(typeof KNOWN_THEME_COLOR_VARIABLES)[number], ThemeColorTokenId>;
